<template>
  <div class="base-phone-table">
    <!-- The count line (docs/page-layout.md): the page's count, then the sort and the page's own
         buttons (`#phone-count`, `#phone-actions`, through TableNormal). -->
    <BasePhoneCountLine v-if="hasCountLine">
      <template v-if="$scopedSlots['phone-count']" #count>
        <slot name="phone-count" />
      </template>
      <template #actions>
        <BasePhoneListControls v-if="sortOptions.length && items.length" :selectable="false" @sort="sortOpen = true" />
        <slot name="phone-actions" />
      </template>
    </BasePhoneCountLine>

    <BaseLoadingBar v-if="loading && !items.length" isSmall />
    <p v-else-if="!items.length" class="base-phone-table__empty">{{ noDataText }}</p>
    <BasePhoneList
      v-else
      :items="listed"
      :item-key="itemKey"
      :sort-open.sync="sortOpen"
      :sort-options="sortOptions"
      :sort-key="sortKey"
      :room-below="false"
      :has-more="listHasMore"
      :loading-more="loadingMore"
      :load-more-error="loadMoreError"
      :pending="pending"
      @sort="$emit('sort', { by: $event.by, desc: $event.desc })"
      @load-more="onLoadMore"
    >
      <template #item="{ item }">
        <BasePhoneRow
          :key="keyOf(item)"
          class="base-phone-table__row"
          :openable="clickable"
          @open="$emit('click:row', item)"
        >
          <template #title>
            <div
              v-for="{ header } in parts.title"
              :key="header.value"
              class="base-phone-row__title base-phone-table__title"
            >
              <Cell :nodes="cell(item, header)" />
            </div>
          </template>

          <div
            v-for="{ header } in parts.subtitle"
            :key="header.value"
            class="base-phone-row__line base-phone-table__subtitle"
          >
            <Cell :nodes="cell(item, header)" />
          </div>
          <div v-if="parts.meta.length" class="base-phone-row__meta base-phone-table__meta">
            <Cell v-for="{ header } in parts.meta" :key="header.value" :nodes="cell(item, header)" />
          </div>
          <dl v-if="parts.fact.length" class="base-phone-row__facts">
            <div v-for="{ header } in parts.fact" :key="header.value" class="base-phone-table__fact">
              <dt>{{ header.text }}</dt>
              <dd><Cell :nodes="cell(item, header)" /></dd>
            </div>
          </dl>
          <div
            v-for="{ header } in parts.block"
            :key="header.value"
            class="base-phone-row__block base-phone-table__block"
          >
            <!-- Buttons say what they do: no "Actions" over them. -->
            <p v-if="header.text && header.value !== 'actions'" class="base-phone-row__block-label">
              {{ header.text }}
            </p>
            <div class="base-phone-table__block-value">
              <Cell :nodes="cell(item, header)" />
            </div>
          </div>

          <template v-if="parts.actions.length" #actions>
            <Cell v-for="{ header } in parts.actions" :key="header.value" :nodes="cell(item, header)" />
          </template>
        </BasePhoneRow>
      </template>
    </BasePhoneList>
  </div>
</template>

<script>
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import BasePhoneCountLine from '@/components/base/BasePhoneCountLine.vue';
import BasePhoneList from '@/components/base/BasePhoneList.vue';
import BasePhoneListControls from '@/components/base/BasePhoneListControls.vue';
import BasePhoneRow from '@/components/base/BasePhoneRow.vue';
import Cell from '@/components/base/phone-table-cell';
import { phoneColumns, phoneSortOptions, PHONE_PAGE_SIZE, PHONE_ROLES, valueAt } from '@/util/phone-table';

// A table on a phone: a list whose lines are its rows, each a BasePhoneRow as in the phone lists
// (the title, a line under it, tags, then "label · value" facts, the actions in the corner). The page
// says which column goes where with `phone` on its headers (see util/phone-table), and its
// `item.<column>` templates fill the cells as they do in the table. TableNormal shows it on phones.
export default {
  name: 'BasePhoneTable',
  components: {
    BaseLoadingBar,
    BasePhoneCountLine,
    BasePhoneList,
    BasePhoneListControls,
    BasePhoneRow,
    Cell,
  },
  props: {
    headers: {
      type: Array,
      required: true,
    },
    // Filtered and sorted already, as the table would show them.
    items: {
      type: Array,
      required: true,
    },
    itemKey: {
      type: String,
      default: 'id',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    noDataText: {
      type: String,
      default: '',
    },
    sortable: {
      type: Boolean,
      default: true,
    },
    // The order shown: "<column>:asc" or "<column>:desc".
    sortKey: {
      type: String,
      default: '',
    },
    // A tap on a row opens it (the table's `click:row`).
    clickable: {
      type: Boolean,
      default: false,
    },
    // The server pages the rows: the list asks for more at its bottom (`load-more`), and says
    // when it has them all.
    loadsMore: {
      type: Boolean,
      default: false,
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
    loadMoreError: {
      type: [Object, Error, String],
      default: null,
    },
    // A new search is running: asking for more now would cancel it.
    pending: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortOpen: false,
      shown: PHONE_PAGE_SIZE,
    };
  },
  computed: {
    // A list held whole still shows a page at a time, more as the reader reaches its bottom: a
    // phone draws a few hundred rows slowly.
    listed() {
      return this.loadsMore ? this.items : this.items.slice(0, this.shown);
    },
    listHasMore() {
      return this.loadsMore ? this.hasMore : this.items.length > this.shown;
    },
    parts() {
      const parts = Object.fromEntries(PHONE_ROLES.map((role) => [role, []]));
      phoneColumns(this.headers).forEach((column) => parts[column.role].push(column));
      return parts;
    },
    hasCountLine() {
      const own = this.$scopedSlots['phone-count'] || this.$scopedSlots['phone-actions'];
      return !!own || (this.sortOptions.length > 0 && this.items.length > 0);
    },
    sortOptions() {
      if (!this.sortable) return [];
      const columns = phoneColumns(this.headers);
      return phoneSortOptions(columns, (column, desc) =>
        this.$t(`table.phone-list.${desc ? 'sort-descending' : 'sort-ascending'}`, { column }),
      );
    },
  },
  methods: {
    valueAt,
    onLoadMore() {
      if (this.loadsMore) this.$emit('load-more');
      else this.shown += PHONE_PAGE_SIZE;
    },
    cell(item, header) {
      const value = valueAt(item, header.value);
      const template = this.$scopedSlots[`item.${header.value}`];
      return template ? template({ item, header, value }) : value;
    },
    keyOf(item) {
      return valueAt(item, this.itemKey);
    },
    // A subtitle, a fact or a block without a value (no text, no icon, no field) leaves no empty line.
    hideEmptyFacts() {
      // Its text, without what the page hides (v-show: Comptes' dash on phones), laid out yet or not.
      const shownText = (value) => {
        const hidden = (node) => {
          for (let el = node.parentElement; el && el !== value.parentElement; el = el.parentElement) {
            if (el.style.display === 'none') return true;
          }
          return false;
        };
        const walker = document.createTreeWalker(value, NodeFilter.SHOW_TEXT);
        let text = '';
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
          if (!hidden(node)) text += node.textContent;
        }
        return text;
      };
      const isBlank = (value) => !shownText(value).trim() && !value.querySelector('img, svg, input, button, .v-input');
      this.$el.querySelectorAll('.base-phone-table__subtitle').forEach((subtitle) => {
        subtitle.classList.toggle('is-empty', isBlank(subtitle));
      });
      this.$el.querySelectorAll('.base-phone-table__fact').forEach((fact) => {
        fact.classList.toggle('is-empty', isBlank(fact.querySelector('dd')));
      });
      this.$el.querySelectorAll('.base-phone-table__block').forEach((block) => {
        block.classList.toggle('is-empty', isBlank(block.querySelector('.base-phone-table__block-value')));
      });
    },
  },
  watch: {
    // Another search or order: from the first page again.
    items() {
      this.shown = PHONE_PAGE_SIZE;
    },
  },
  mounted() {
    this.hideEmptyFacts();
  },
  updated() {
    this.hideEmptyFacts();
  },
};
</script>

<style lang="scss">
.base-phone-table {
  &__empty {
    margin: 0;
    padding: 16px;
    font-size: 14px;
    color: $color-slate-500;
  }

  // A cell may hold more than text (a tag beside the name, a second line): in full.
  &__title {
    display: block;
    overflow: visible;
    overflow-wrap: anywhere;
  }

  &__subtitle {
    white-space: normal;
    overflow-wrap: anywhere;

    &.is-empty {
      display: none;
    }
  }

  &__fact {
    display: contents;

    &.is-empty {
      display: none;
    }
  }

  &__block.is-empty {
    display: none;
  }

  // A row's own buttons and fields stay above its stretched title.
  &__row
    .base-phone-row__body
    :is(a, button, input, label, select, textarea, [role='button'], .v-input):not(.base-phone-row__open) {
    position: relative;
    z-index: 1;
  }

  // The actions side by side (a pin and the ⋯), in the row's top corner by the title as in the
  // phone lists, not in the middle of a tall row.
  &__row .base-phone-row__actions {
    grid-auto-flow: column;
    align-content: start;
  }
}
</style>
